import React, { useEffect, useState } from "react";
import { Users, ShieldCheck, Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { ConfirmCard } from "@/components/admin/YalolaCards";

// Lot 774 — équipes support : le super admin / un manager désigne un chef d'équipe et lui confie des agents.
// Tout agent non affecté appartient à « Yalola Support » (chefs : les super admins).
const AgentChip = ({ a, lead = false }) => (
  <span className="chip text-[10px] inline-flex items-center gap-1" style={lead ? { background: "#E0E7FF", color: "#3730A3" } : {}} data-testid={`team-agent-${a.id}`}>
    {lead && <ShieldCheck size={10} />}{a.name || "—"}
  </span>
);

const TeamDialog = ({ open, onOpenChange, team, agents, onSaved }) => {
  const [name, setName] = useState("");
  const [leadId, setLeadId] = useState("");
  const [ids, setIds] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setName(team?.name || ""); setLeadId(team?.lead_id || ""); setIds(team?.agent_ids || []);
  }, [open, team]);
  const toggle = (id) => setIds((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
  const save = async () => {
    setBusy(true);
    try {
      const body = { name, lead_id: leadId, agent_ids: ids.filter((x) => x !== leadId) };
      if (team?.id) await api.put(`/admin/support-teams/${team.id}`, body); else await api.post("/admin/support-teams", body);
      toast.success(team?.id ? "Équipe mise à jour" : "Équipe créée — le chef d'équipe est prévenu");
      onOpenChange(false); onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || e?.friendlyMessage || "Erreur"); }
    finally { setBusy(false); }
  };
  const candidates = agents.filter((a) => !a.is_super);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="team-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ShieldCheck size={18} style={{ color: "var(--yalola-primary)" }} /> {team?.id ? "Modifier l'équipe" : "Nouvelle équipe support"}</DialogTitle>
          <DialogDescription>Le chef d'équipe autorise (ou refuse) les reprises de tickets tenus par ses agents. Un agent n'appartient qu'à une seule équipe.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-sm font-semibold">Nom de l'équipe</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex. : Support francophone" className="mt-1.5" data-testid="team-name-input" /></div>
          <div>
            <Label className="text-sm font-semibold">Chef d'équipe</Label>
            <select value={leadId} onChange={(e) => setLeadId(e.target.value)} className="mt-1.5 w-full rounded-xl border px-3 py-2 text-sm" style={{ borderColor: "var(--yalola-border)", background: "var(--yalola-surface)" }} data-testid="team-lead-select">
              <option value="">— choisir un agent —</option>
              {candidates.map((a) => <option key={a.id} value={a.id}>{a.name} ({(a.roles || []).join(", ")})</option>)}
            </select>
          </div>
          <div>
            <Label className="text-sm font-semibold">Agents à sa charge</Label>
            <div className="mt-1.5 grid grid-cols-1 sm:grid-cols-2 gap-1.5" data-testid="team-agents-list">
              {candidates.filter((a) => a.id !== leadId).map((a) => (
                <label key={a.id} className="flex items-center gap-2 text-sm rounded-xl px-2.5 py-1.5 cursor-pointer" style={{ background: "var(--yalola-surface-alt)" }}>
                  <input type="checkbox" checked={ids.includes(a.id)} onChange={() => toggle(a.id)} data-testid={`team-agent-check-${a.id}`} />
                  <span className="min-w-0 truncate">{a.name}</span>
                  {a.team_id && a.team_id !== "default" && a.team_id !== team?.id && <span className="chip text-[9px] ml-auto shrink-0" title="Sera retiré de son équipe actuelle">{a.team_name}</span>}
                </label>
              ))}
              {candidates.length === 0 && <div className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>Aucun agent support pour l'instant (rôle Support / Modérateur / Manager).</div>}
            </div>
          </div>
        </div>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" className="rounded-full" onClick={() => onOpenChange(false)} disabled={busy}>Annuler</Button>
          <Button className="pill-primary rounded-full" onClick={save} disabled={busy || !name.trim() || !leadId} data-testid="team-save-btn">{busy && <Loader2 size={14} className="mr-1 animate-spin" />}{team?.id ? "Enregistrer" : "Créer l'équipe"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const SupportTeamsCard = ({ me }) => {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null);     // null fermé · {} nouvelle · team
  const [toDelete, setToDelete] = useState(null);
  const load = () => api.get("/admin/support-teams").then((r) => setData(r.data)).catch(() => setData({ teams: [], agents: [], default_team: null, can_manage: false, guard_on: true }));
  useEffect(() => { load(); }, []);
  if (!data) return null;
  const { teams = [], default_team: def, agents = [], can_manage: canManage, guard_on: guardOn } = data;
  const myTeam = agents.find((a) => a.id === me)?.team_name;
  const remove = async () => {
    try { await api.delete(`/admin/support-teams/${toDelete.id}`); toast.success("Équipe supprimée — ses agents reviennent dans Yalola Support"); setToDelete(null); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };
  return (
    <div className="yalola-card p-4 space-y-3" data-testid="support-teams-card" data-guard={guardOn ? "1" : "0"}>
      <div className="flex items-center gap-2 flex-wrap text-xs">
        <Users size={14} style={{ color: "var(--yalola-primary)" }} />
        <b>Équipes support</b>
        <span style={{ color: "var(--yalola-text-muted)" }}>· le chef d'équipe autorise les reprises de tickets de ses agents</span>
        {myTeam && <span className="chip text-[10px]" data-testid="support-teams-mine">Mon équipe : {myTeam}</span>}
        {!guardOn && <span className="chip text-[10px]" style={{ background: "#FEE2E2", color: "#991B1B" }} data-testid="support-teams-guard-off">garde-fou désactivé : reprise directe pour tous</span>}
        {canManage && <Button size="sm" className="pill-primary rounded-full h-7 px-2.5 ml-auto" onClick={() => setEditing({})} data-testid="team-new-btn"><Plus size={12} className="mr-1" /> Nouvelle équipe</Button>}
      </div>
      <div className="grid gap-2 sm:grid-cols-2">
        {def && (
          <div className="rounded-xl p-3 text-xs space-y-1.5" style={{ background: "var(--yalola-surface-alt)" }} data-testid="support-team-default">
            <div className="font-semibold flex items-center gap-1.5"><ShieldCheck size={12} style={{ color: "#3730A3" }} /> {def.name} <span className="chip text-[9px]">par défaut</span></div>
            <div style={{ color: "var(--yalola-text-muted)" }}>Chef{def.leads?.length > 1 ? "s" : ""} : {def.leads?.map((l) => l.name).join(", ") || "super admin"}</div>
            <div className="flex flex-wrap gap-1">{def.agents?.length ? def.agents.map((a) => <AgentChip key={a.id} a={a} />) : <span style={{ color: "var(--yalola-text-muted)" }}>Tous les agents sont affectés à une équipe.</span>}</div>
          </div>
        )}
        {teams.map((t) => (
          <div key={t.id} className="rounded-xl p-3 text-xs space-y-1.5" style={{ background: "var(--yalola-surface-alt)" }} data-testid={`support-team-${t.id}`}>
            <div className="font-semibold flex items-center gap-1.5">
              <ShieldCheck size={12} style={{ color: "#3730A3" }} /> {t.name}
              {canManage && (
                <span className="ml-auto flex gap-1">
                  <button type="button" className="opacity-70 hover:opacity-100" title="Modifier" onClick={() => setEditing(t)} data-testid={`team-edit-${t.id}`}><Pencil size={12} /></button>
                  <button type="button" className="opacity-70 hover:opacity-100" title="Supprimer" onClick={() => setToDelete(t)} data-testid={`team-delete-${t.id}`}><Trash2 size={12} style={{ color: "#B91C1C" }} /></button>
                </span>
              )}
            </div>
            <div style={{ color: "var(--yalola-text-muted)" }}>Chef : <b style={{ color: "var(--yalola-text)" }}>{t.lead?.name || t.lead_name || "—"}</b> · {t.agents?.length || 0} agent(s)</div>
            <div className="flex flex-wrap gap-1">{(t.agents || []).map((a) => <AgentChip key={a.id} a={a} />)}</div>
          </div>
        ))}
      </div>
      <TeamDialog open={editing !== null} onOpenChange={(v) => !v && setEditing(null)} team={editing?.id ? editing : null} agents={agents} onSaved={load} />
      <ConfirmCard open={!!toDelete} onOpenChange={(v) => !v && setToDelete(null)} title={`Supprimer l'équipe « ${toDelete?.name} » ?`} description="Ses agents reviennent dans l'équipe par défaut Yalola Support (chefs : super admins). Les tickets ne bougent pas." confirmLabel="Supprimer" danger onConfirm={remove} testId="team-delete-card" />
    </div>
  );
};
